import React, { useState } from "react";
import { motion } from "framer-motion";

const MoodTrackingGraph: React.FC = () => {
    const [selectedMood, setSelectedMood] = useState<string | null>(null);

    const moods = [
        { emoji: "😊", label: "شاد", value: "happy", color: "#32D74B" },
        { emoji: "😌", label: "آرام", value: "calm", color: "#66D4CF" },
        { emoji: "😐", label: "عادی", value: "neutral", color: "#9EA3A9" },
        { emoji: "😔", label: "ناراحت", value: "sad", color: "#FFD60A" },
        { emoji: "😤", label: "عصبانی", value: "angry", color: "#FF3B30" },
    ];

    const weeklyMoods = [
        { day: "ش", mood: "happy", sleep: 7.5, stress: 25 },
        { day: "ی", mood: "calm", sleep: 8, stress: 20 },
        { day: "د", mood: "neutral", sleep: 6, stress: 55 },
        { day: "س", mood: "sad", sleep: 5.5, stress: 70 },
        { day: "چ", mood: "calm", sleep: 7, stress: 30 },
        { day: "پ", mood: "happy", sleep: 8.5, stress: 15 },
        { day: "ج", mood: "happy", sleep: 8, stress: 20 },
    ];

    const insights = [
        {
            icon: "😴",
            text: "در روزهایی که کمتر از ۶ ساعت خوابیده‌اید، سطح تحریک‌پذیری شما ۴۰٪ بالاتر بوده است.",
        },
        {
            icon: "🏃",
            text: "فعالیت بدنی روزانه با بهبود ۳۵٪ خلق‌و‌خو همراه بوده است.",
        },
        {
            icon: "☀️",
            text: "بهترین خلق‌و‌خو شما در روزهای پنج‌شنبه و جمعه ثبت شده است.",
        },
    ];

    const getMoodColor = (mood: string) => {
        const found = moods.find((m) => m.value === mood);
        return found?.color || "#9EA3A9";
    };

    return (
        <div className="space-y-3" dir="rtl">
            <div>
                <h4 className="font-medium text-[#002B5C] mb-3">خلق‌و‌خوی امروز</h4>

                {/* Mood Selector */}
                <div className="flex justify-between gap-2 mb-4">
                    {moods.map((mood) => (
                        <motion.button
                            key={mood.value}
                            className={`flex-1 p-3 rounded-[16px] text-center transition-all ${
                                selectedMood === mood.value ? "bg-gradient-to-br from-[#0072CE]/20 to-[#00E5FF]/10 border-2 border-[#0072CE]" : "bg-[#ECEFF1] border-2 border-transparent"
                            }`}
                            onClick={() => setSelectedMood(mood.value)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <div className="text-2xl mb-1">{mood.emoji}</div>
                            <div className="text-xs text-[#4A5568]">{mood.label}</div>
                        </motion.button>
                    ))}
                </div>

                {/* Weekly Mood Chart */}
                <h4 className="font-medium text-[#002B5C] mb-2">روند هفتگی</h4>
                <div className="bg-[#ECEFF1] p-4 rounded-[16px] shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                    <div className="flex items-end justify-between h-32">
                        {weeklyMoods.map((data, i) => {
                            const moodData = moods.find((m) => m.value === data.mood);
                            return (
                                <div key={i} className="flex flex-col items-center gap-2">
                                    <motion.div
                                        className="text-3xl cursor-pointer"
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: i * 0.1 }}
                                        whileHover={{ scale: 1.3 }}
                                        style={{
                                            filter: `drop-shadow(0 2px 4px ${getMoodColor(data.mood)}40)`,
                                        }}
                                    >
                                        {moodData?.emoji}
                                    </motion.div>
                                    <span className="text-xs text-[#9EA3A9]">{data.day}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Correlation Analysis */}
                <h4 className="font-medium text-[#002B5C] mt-4 mb-2">تحلیل ارتباطات</h4>
                <div className="space-y-2">
                    <div className="bg-[#ECEFF1] p-3 rounded-[16px] shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-[#4A5568]">تاثیر خواب</span>
                            <span className="text-sm font-medium text-[#0072CE]">بالا</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex-1 bg-white h-2 rounded-full">
                                <motion.div className="h-2 rounded-full bg-gradient-to-r from-[#0072CE] to-[#00E5FF]" initial={{ width: 0 }} animate={{ width: "85%" }} transition={{ duration: 1 }} />
                            </div>
                            <span className="text-xs text-[#9EA3A9]">۸۵٪</span>
                        </div>
                    </div>

                    <div className="bg-[#ECEFF1] p-3 rounded-[16px] shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-[#4A5568]">تاثیر فعالیت بدنی</span>
                            <span className="text-sm font-medium text-[#32D74B]">بالا</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex-1 bg-white h-2 rounded-full">
                                <motion.div
                                    className="h-2 rounded-full bg-gradient-to-r from-[#32D74B] to-[#66D4CF]"
                                    initial={{ width: 0 }}
                                    animate={{ width: "75%" }}
                                    transition={{ duration: 1, delay: 0.2 }}
                                />
                            </div>
                            <span className="text-xs text-[#9EA3A9]">۷۵٪</span>
                        </div>
                    </div>

                    <div className="bg-[#ECEFF1] p-3 rounded-[16px] shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-[#4A5568]">تاثیر استرس</span>
                            <span className="text-sm font-medium text-[#FF9500]">متوسط</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex-1 bg-white h-2 rounded-full">
                                <motion.div
                                    className="h-2 rounded-full bg-gradient-to-r from-[#FF9500] to-[#FF3B30]"
                                    initial={{ width: 0 }}
                                    animate={{ width: "60%" }}
                                    transition={{ duration: 1, delay: 0.4 }}
                                />
                            </div>
                            <span className="text-xs text-[#9EA3A9]">۶۰٪</span>
                        </div>
                    </div>
                </div>

                {/* AI Insights */}
                <h4 className="font-medium text-[#002B5C] mt-4 mb-2">بینش‌های هوشمند</h4>
                <div className="space-y-2">
                    {insights.map((insight, i) => (
                        <motion.div
                            key={i}
                            className="flex items-start gap-3 p-3 bg-gradient-to-r from-[#A7FFEB]/20 to-[#66D4CF]/10 rounded-[16px] border border-[#66D4CF]/30"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                        >
                            <span className="text-2xl flex-shrink-0">{insight.icon}</span>
                            <p className="text-xs text-[#4A5568] leading-relaxed">{insight.text}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MoodTrackingGraph;
